import { string } from 'yup';

import { Customer, Maybe } from '../gql/generated/graphql';

export const replaceUnderscores = (str: string, replacement = ' ') => {
  return str.replace(/_/g, replacement);
};

export const capitalizeFirstLetter = (str: Maybe<string>) => {
  return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
};

type NameParts = Partial<Pick<Customer, 'first_name' | 'last_name'>> | null | undefined;

export const initialName = (customer: NameParts) => {
  const first = capitalizeFirstLetter(customer?.first_name?.trim());
  const last = customer?.last_name?.trim() ?? '';
  if (!last) return first;

  return `${first} ${last[0].toUpperCase()}.`;
};

export const stripCountySuffix = (str: Maybe<string>) => {
  return str ? str.replace(/\s+county$/i, '') : '';
};

// Collapse any run of whitespace containing a line break into a single space so multi-line
// text reads as one line.
export const toSingleLine = (str: string) => str.replace(/\s*[\r\n]+\s*/g, ' ').trim();

export const indefiniteArticle = (name: string): string => (/^[aeio]/i.test(name) ? 'an' : 'a');

export const capitalizeWords = (str: string) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const toHex = (str: string) => {
  const result: string[] = [];
  for (let i = 0; i < str.length; ++i) {
    result.push(str.charCodeAt(i).toString(16).padStart(2, '0'));
  }

  return result.join('');
};

export const notRequiredString = string().notRequired().nullable();
export const requiredStringMaxCharsValidation = ({
  max = 100,
  fieldName = 'Field',
}: {
  max?: number;
  fieldName?: string;
}) =>
  string()
    .max(max, `Must be less than ${max} characters`)
    .nullable()
    .required(`${fieldName} is required`);

export const snakeCaseToHuman = (input: string | null | undefined) => {
  if (!input) return '';

  return input
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const formatPhoneInput = (text: string, mask?: string): string => {
  const digitsOnly = text.replace(/\D/g, '');

  // Remove all leading "1"s from the start of the number sequence
  let cleanedDigits = digitsOnly;
  while (cleanedDigits.startsWith('1') && cleanedDigits.length > 0) {
    cleanedDigits = cleanedDigits.slice(1);
  }

  // If no mask provided, return the cleaned digits
  if (!mask || cleanedDigits.length === 0) {
    return cleanedDigits;
  }

  // Reconstruct the formatted text based on the mask
  let formatted = '';
  let digitIndex = 0;

  for (let i = 0; i < mask.length; i += 1) {
    if (mask[i] === '9' && digitIndex < cleanedDigits.length) {
      formatted += cleanedDigits[digitIndex];
      digitIndex += 1;
    } else if (mask[i] !== '9') {
      // Only add formatting characters if we still have digits to place
      if (digitIndex < cleanedDigits.length) {
        formatted += mask[i];
      }
    } else {
      // No more digits to place, stop here
      break;
    }
  }

  return formatted;
};

export const normalizePhone = (phone: string): string => {
  const digits = formatPhoneInput(phone);
  return digits.length === 10 ? formatPhoneInput(digits, '(999) 999-9999') : phone;
};

export const formatPhoneNumber10Digits = (phoneNumber: string): string => {
  const digits = phoneNumber.replace(/\D/g, '');
  return digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
};

export const formatPhoneNumberWithCountryCode = (phoneNumber?: string): string | undefined => {
  if (phoneNumber == null) {
    return undefined;
  }

  const digits = formatPhoneNumber10Digits(phoneNumber);
  return digits.length === 10 ? `1${digits}` : digits;
};
